import { useCallback, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import type { EnumKeys } from './useAuthorization.type';

/**
 * Custom hook to fetch and check user authorizations.
 *
 * This hook fetches user-specific authorization attributes from an API using the user's ID from a cookie.
 * It provides a method to check if the user has specific authorizations and a loading state while data is being fetched.
 *
 * @param authorizationEnum - Enum type to check authorizations against.
 * @returns {Object} An object containing:
 * - `isAuthorizationsLoading` (boolean): Indicates whether the authorization data is currently being loaded.
 * - `getAuthorization` (function): A function that accepts an array of enum values and returns an object mapping each authorization key to a boolean.
 */
export const useAuthorization = <E extends Record<string, string>>(authorizationEnum: E) => {
  // Retrieve the user's authentication cookie
  const sessionData = useSession();
  const featureAttributes = useMemo(() => sessionData.data?.user?.featureAttributes ?? [], [sessionData]);

  /**
   * Checks if the user has specific authorizations.
   *
   * This function accepts a variable number of enum values and returns an object where each key corresponds
   * to an authorization, with `true` if the user has that authorization or `false` otherwise.
   *
   * @param {...E[keyof E]} authorizationsToCheck - List of enum values to check.
   * @returns {Record<EnumKeys<E, typeof authorizationsToCheck[number]>, boolean>} An object mapping each enum key to a boolean.
   */
  const getAuthorization = useCallback(
    <K extends E[keyof E]>(...authorizationsToCheck: K[]): Record<EnumKeys<E, K>, boolean> => {
      // Map enum values to their corresponding keys
      const enumMap = Object.entries(authorizationEnum).reduce((acc, [key, value]) => {
        acc[value as E[keyof E]] = key as keyof E;
        return acc;
      }, {} as Record<E[keyof E], keyof E>);

      // Reduce the authorizations to check, using only the specified enum keys in the result
      return authorizationsToCheck.reduce((prev, curr) => {
        const key = enumMap[curr];
        prev[key as EnumKeys<E, K>] = featureAttributes.includes(curr) || false;
        return prev;
      }, {} as Record<EnumKeys<E, K>, boolean>);
    },
    [featureAttributes, authorizationEnum]
  );

  return {
    isAuthorizationsLoading: false,
    getAuthorization,
  };
};
